import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormGroup,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  triggerDailyPulseBackfill,
  fetchDailyPulseBackfillStatus,
  triggerBackendSummaryBackfill,
  fetchBackendSummaryBackfillStatus,
  triggerLeadCzvBackfill,
  fetchLeadCzvBackfillStatus,
  triggerFacebookSummaryBackfill,
  fetchFacebookSummaryBackfillStatus,
  triggerDashboardDataDeletion,
  fetchDashboardDataDeletionStatus,
  triggerBackendDataDeletion,
  fetchBackendDataDeletionStatus,
} from '@src/api';
import defaultClient from '@src/api/clients/default';
import { ClientDropdown } from '@src/components';

const POLL_INTERVAL_MS = 12000;
const PLATFORM_OPTIONS = [
  { key: 'facebook', label: 'Facebook' },
  { key: 'google', label: 'Google' },
  { key: 'bing', label: 'Bing' },
  { key: 'tiktok', label: 'TikTok' },
];
const DAILY_MODELS = [
  { key: 'client', label: 'Client Summary' },
  { key: 'channel', label: 'Channel Summary' },
  { key: 'campaign', label: 'Campaign Summary' },
  { key: 'dma', label: 'DMA Summary' },
  { key: 'state', label: 'State Summary' },
  { key: 'city', label: 'City Summary' },
  { key: 'county', label: 'County Summary' },
  { key: 'territory', label: 'Territory Summary' },
  { key: 'territory_campaign', label: 'Territory Campaign Summary' },
];
const BACKEND_MODELS = [
  { key: 'client', label: 'Client Summary' },
  { key: 'channel', label: 'Channel Summary' },
  { key: 'vertical', label: 'Vertical Summary' },
  { key: 'campaign', label: 'Campaign Summary' },
  { key: 'dma', label: 'DMA Summary' },
  { key: 'territory', label: 'Territory Summary' },
  { key: 'territory_campaign', label: 'Territory Campaign Summary' },
  { key: 'state', label: 'State Summary' },
  { key: 'city', label: 'City Summary' },
  { key: 'county', label: 'County Summary' },
];
const DELETE_MODELS = [
  { key: 'client', label: 'Client Summary' },
  { key: 'territory', label: 'Territory Summary' },
  { key: 'territory_campaign', label: 'Territory Campaign Summary' },
  { key: 'campaign', label: 'Campaign Summary' },
  { key: 'daily_pulse_ad', label: 'Daily Pulse Ad Summary' },
  { key: 'dma', label: 'DMA Summary' },
  { key: 'channel', label: 'Channel Summary' },
  { key: 'vertical', label: 'Vertical Summary' },
  { key: 'state', label: 'State Summary' },
  { key: 'city', label: 'City Summary' },
  { key: 'county', label: 'County Summary' },
  { key: 'client_performance', label: 'Client Performance Summary' },
  { key: 'today_performance', label: 'Today Performance Summary' },
  { key: 'dashboard_overview', label: 'Dashboard Overview' },
];

const toYMD = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = `${d.getMonth() + 1}`.padStart(2, '0');
  const dd = `${d.getDate()}`.padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  return {
    startDate: toYMD(start),
    endDate: toYMD(end),
  };
};

const getStateSeverity = (state) => {
  if (state === 'success') return 'success';
  if (state === 'error') return 'error';
  if (state === 'running') return 'info';
  return 'warning';
};

const getDailyProgress = (statusData) => {
  if (!statusData?.expected_days) return 0;
  return Math.min(
    100,
    Math.round(((statusData.succeeded + statusData.failed) / statusData.expected_days) * 100)
  );
};

export default function DataBackfillPage() {
  const defaults = useMemo(getDefaultDateRange, []);
  const platformKeys = useMemo(() => PLATFORM_OPTIONS.map((platform) => platform.key), []);
  const dailyModelKeys = useMemo(() => DAILY_MODELS.map((model) => model.key), []);
  const backendModelKeys = useMemo(() => BACKEND_MODELS.map((model) => model.key), []);
  const deleteModelKeys = useMemo(() => DELETE_MODELS.map((model) => model.key), []);

  const [clientId, setClientId] = useState('');
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [skipPlatformImport, setSkipPlatformImport] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState(PLATFORM_OPTIONS.map((x) => x.key));
  const [dailySelectedModels, setDailySelectedModels] = useState(DAILY_MODELS.map((x) => x.key));
  const [backendSelectedModels, setBackendSelectedModels] = useState(BACKEND_MODELS.map((x) => x.key));

  const [dailyRequestId, setDailyRequestId] = useState('');
  const [dailyStatus, setDailyStatus] = useState(null);
  const [dailyAlert, setDailyAlert] = useState(null);
  const [dailyPolling, setDailyPolling] = useState(false);
  const [dailyLoading, setDailyLoading] = useState(false);

  const [backendRequestId, setBackendRequestId] = useState('');
  const [backendStatus, setBackendStatus] = useState(null);
  const [backendAlert, setBackendAlert] = useState(null);
  const [backendPolling, setBackendPolling] = useState(false);
  const [backendLoading, setBackendLoading] = useState(false);

  const [leadCzvRequestId, setLeadCzvRequestId] = useState('');
  const [leadCzvStatus, setLeadCzvStatus] = useState(null);
  const [leadCzvAlert, setLeadCzvAlert] = useState(null);
  const [leadCzvPolling, setLeadCzvPolling] = useState(false);
  const [leadCzvLoading, setLeadCzvLoading] = useState(false);

  const [facebookRequestId, setFacebookRequestId] = useState('');
  const [facebookStatus, setFacebookStatus] = useState(null);
  const [facebookAlert, setFacebookAlert] = useState(null);
  const [facebookPolling, setFacebookPolling] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [includeFacebookMetadata, setIncludeFacebookMetadata] = useState(true);
  const [facebookAllClients, setFacebookAllClients] = useState(true);

  const [deleteRequestId, setDeleteRequestId] = useState('');
  const [deleteStatus, setDeleteStatus] = useState(null);
  const [deleteAlert, setDeleteAlert] = useState(null);
  const [deletePolling, setDeletePolling] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSelectedModels, setDeleteSelectedModels] = useState(DELETE_MODELS.map((x) => x.key));

  const [backendDeleteRequestId, setBackendDeleteRequestId] = useState('');
  const [backendDeleteStatus, setBackendDeleteStatus] = useState(null);
  const [backendDeleteAlert, setBackendDeleteAlert] = useState(null);
  const [backendDeletePolling, setBackendDeletePolling] = useState(false);
  const [backendDeleteLoading, setBackendDeleteLoading] = useState(false);
  const [parentCompanyId, setParentCompanyId] = useState('');
  const [parentCompanyOptions, setParentCompanyOptions] = useState([]);

  const hasValidRange = !!startDate && !!endDate && startDate <= endDate;
  const hasScope = !!clientId || !!parentCompanyId;
  const hasDailyModels = dailySelectedModels.length > 0;
  const hasSelectedPlatforms = selectedPlatforms.length > 0;
  const hasBackendModels = backendSelectedModels.length > 0;
  const hasDeleteModels = deleteSelectedModels.length > 0;
  const scopePayload = useMemo(() => (
    parentCompanyId
      ? { parent_company_id: parentCompanyId }
      : clientId
        ? { client_id: clientId }
        : {}
  ), [clientId, parentCompanyId]);

  const toggleModel = (setter, selected, modelKey) => {
    setter(
      selected.includes(modelKey)
        ? selected.filter((key) => key !== modelKey)
        : [...selected, modelKey]
    );
  };

  const toggleAllModels = (setter, selected, allKeys) => {
    const allSelected = allKeys.every((key) => selected.includes(key));
    setter(allSelected ? [] : allKeys);
  };

  const pollDailyStatus = useCallback(async () => {
    if (!dailyRequestId || !hasScope || !hasValidRange) return;
    try {
      const res = await fetchDailyPulseBackfillStatus({
        ...scopePayload,
        start_date: startDate,
        end_date: endDate,
        request_id: dailyRequestId,
      });
      const next = res?.data || null;
      setDailyStatus(next);
      if (next?.state === 'success' || next?.state === 'error') {
        setDailyPolling(false);
      }
    } catch (error) {
      console.error('Failed to poll daily pulse status', error);
      setDailyPolling(false);
      setDailyAlert({ severity: 'error', text: 'Failed to load daily pulse status.' });
    }
  }, [dailyRequestId, endDate, hasScope, hasValidRange, scopePayload, startDate]);

  const pollBackendStatus = useCallback(async () => {
    if (!backendRequestId || !hasScope) return;
    try {
      const res = await fetchBackendSummaryBackfillStatus({
        ...scopePayload,
        request_id: backendRequestId,
      });
      const next = res?.data || null;
      setBackendStatus(next);
      if (next?.state === 'success' || next?.state === 'error') {
        setBackendPolling(false);
      }
    } catch (error) {
      console.error('Failed to poll backend summary status', error);
      setBackendPolling(false);
      setBackendAlert({ severity: 'error', text: 'Failed to load backend summary status.' });
    }
  }, [backendRequestId, hasScope, scopePayload]);

  const pollFacebookStatus = useCallback(async () => {
    if (!facebookRequestId || !hasValidRange) return;
    try {
      const res = await fetchFacebookSummaryBackfillStatus({
        request_id: facebookRequestId,
        start_date: startDate,
        end_date: endDate,
      });
      const next = res?.data || null;
      setFacebookStatus(next);
      if (next?.state === 'success' || next?.state === 'error') {
        setFacebookPolling(false);
      }
    } catch (error) {
      console.error('Failed to poll Facebook summary status', error);
      setFacebookPolling(false);
      setFacebookAlert({ severity: 'error', text: 'Failed to load Facebook summary status.' });
    }
  }, [endDate, facebookRequestId, hasValidRange, startDate]);

  const pollLeadCzvStatus = useCallback(async () => {
    if (!leadCzvRequestId || !hasScope) return;
    try {
      const res = await fetchLeadCzvBackfillStatus({
        ...scopePayload,
        request_id: leadCzvRequestId,
      });
      const next = res?.data || null;
      setLeadCzvStatus(next);
      if (next?.state === 'success' || next?.state === 'error') {
        setLeadCzvPolling(false);
      }
    } catch (error) {
      console.error('Failed to poll lead CZV backfill status', error);
      setLeadCzvPolling(false);
      setLeadCzvAlert({ severity: 'error', text: 'Failed to load lead CZV backfill status.' });
    }
  }, [hasScope, leadCzvRequestId, scopePayload]);

  const pollDeleteStatus = useCallback(async () => {
    if (!deleteRequestId || !hasScope) return;
    try {
      const res = await fetchDashboardDataDeletionStatus({
        ...scopePayload,
        request_id: deleteRequestId,
      });
      const next = res?.data || null;
      setDeleteStatus(next);
      if (next?.state === 'success' || next?.state === 'error') {
        setDeletePolling(false);
      }
    } catch (error) {
      console.error('Failed to poll dashboard delete status', error);
      setDeletePolling(false);
      setDeleteAlert({ severity: 'error', text: 'Failed to load delete status.' });
    }
  }, [deleteRequestId, hasScope, scopePayload]);

  const pollBackendDeleteStatus = useCallback(async () => {
    if (!backendDeleteRequestId || !hasScope) return;
    try {
      const res = await fetchBackendDataDeletionStatus({
        ...scopePayload,
        request_id: backendDeleteRequestId,
      });
      const next = res?.data || null;
      setBackendDeleteStatus(next);
      if (next?.state === 'success' || next?.state === 'error') {
        setBackendDeletePolling(false);
      }
    } catch (error) {
      console.error('Failed to poll backend data delete status', error);
      setBackendDeletePolling(false);
      setBackendDeleteAlert({ severity: 'error', text: 'Failed to load backend data delete status.' });
    }
  }, [backendDeleteRequestId, hasScope, scopePayload]);

  useEffect(() => {
    let active = true;
    defaultClient.request({
      url: 'dashboard/clients/',
      method: 'GET',
      payload: {
        fields: 'parent_company,parent_company_name',
        limit: 500,
      },
    }).then((res) => {
      if (!active) return;
      const rows = Array.isArray(res?.data) ? res.data : res?.data?.results || [];
      const unique = new Map();
      rows.forEach((row) => {
        if (row?.parent_company && row?.parent_company_name) {
          unique.set(String(row.parent_company), row.parent_company_name);
        }
      });
      setParentCompanyOptions(
        Array.from(unique.entries())
          .map(([value, label]) => ({ value, label }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    }).catch((error) => {
      console.error('Failed to load parent companies', error);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!dailyPolling) return undefined;
    pollDailyStatus();
    const id = setInterval(pollDailyStatus, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [dailyPolling, pollDailyStatus]);

  useEffect(() => {
    if (!backendPolling) return undefined;
    pollBackendStatus();
    const id = setInterval(pollBackendStatus, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [backendPolling, pollBackendStatus]);

  useEffect(() => {
    if (!facebookPolling) return undefined;
    pollFacebookStatus();
    const id = setInterval(pollFacebookStatus, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [facebookPolling, pollFacebookStatus]);

  useEffect(() => {
    if (!leadCzvPolling) return undefined;
    pollLeadCzvStatus();
    const id = setInterval(pollLeadCzvStatus, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [leadCzvPolling, pollLeadCzvStatus]);

  useEffect(() => {
    if (!deletePolling) return undefined;
    pollDeleteStatus();
    const id = setInterval(pollDeleteStatus, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [deletePolling, pollDeleteStatus]);

  useEffect(() => {
    if (!backendDeletePolling) return undefined;
    pollBackendDeleteStatus();
    const id = setInterval(pollBackendDeleteStatus, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [backendDeletePolling, pollBackendDeleteStatus]);

  const handleDailyBackfill = async () => {
    if (!hasScope || !hasValidRange || !hasDailyModels || (!skipPlatformImport && !hasSelectedPlatforms) || dailyLoading) return;
    setDailyLoading(true);
    setDailyAlert(null);
    setDailyStatus(null);
    try {
      const res = await triggerDailyPulseBackfill({
        ...scopePayload,
        start_date: startDate,
        end_date: endDate,
        use_celery: true,
        sequential: true,
        selected_models: dailySelectedModels,
        skip_platform_import: skipPlatformImport,
        selected_platforms: selectedPlatforms,
      });
      const data = res?.data || {};
      const requestId = data?.request_id || '';
      setDailyRequestId(requestId);
      setDailyAlert({
        severity: 'success',
        text: `${data?.detail || 'Daily pulse backfill scheduled.'}`,
      });
      if (requestId) {
        setDailyPolling(true);
      }
    } catch (error) {
      console.error('Failed to schedule daily pulse backfill', error);
      const payload = error?.response?.data;
      const detail =
        payload?.detail ||
        (typeof payload === 'string' ? payload : '') ||
        Object.values(payload || {}).flat().join(' ') ||
        'Failed to schedule daily pulse backfill.';
      setDailyAlert({ severity: 'error', text: detail });
      setDailyPolling(false);
    } finally {
      setDailyLoading(false);
    }
  };

  const handleBackendBackfill = async () => {
    if (!hasScope || !hasValidRange || !hasBackendModels || backendLoading) return;
    setBackendLoading(true);
    setBackendAlert(null);
    setBackendStatus(null);
    try {
      const res = await triggerBackendSummaryBackfill({
        ...scopePayload,
        start_date: startDate,
        end_date: endDate,
        refresh_all_time: true,
        selected_models: backendSelectedModels,
      });
      const data = res?.data || {};
      const requestId = data?.request_id || '';
      setBackendRequestId(requestId);
      setBackendAlert({
        severity: 'success',
        text: `${data?.detail || 'Backend summary backfill scheduled.'}`,
      });
      if (requestId) {
        setBackendPolling(true);
      }
    } catch (error) {
      console.error('Failed to schedule backend summary backfill', error);
      const payload = error?.response?.data;
      const detail =
        payload?.detail ||
        (typeof payload === 'string' ? payload : '') ||
        Object.values(payload || {}).flat().join(' ') ||
        'Failed to schedule backend summary backfill.';
      setBackendAlert({ severity: 'error', text: detail });
      setBackendPolling(false);
    } finally {
      setBackendLoading(false);
    }
  };

  const handleFacebookBackfill = async () => {
    if (!hasValidRange || facebookLoading || (!facebookAllClients && !hasScope)) return;
    setFacebookLoading(true);
    setFacebookAlert(null);
    setFacebookStatus(null);
    try {
      const res = await triggerFacebookSummaryBackfill({
        start_date: startDate,
        end_date: endDate,
        include_metadata: includeFacebookMetadata,
        ...(facebookAllClients ? {} : scopePayload),
      });
      const data = res?.data || {};
      const requestId = data?.request_id || '';
      setFacebookRequestId(requestId);
      setFacebookAlert({
        severity: 'success',
        text: `${data?.detail || 'Facebook summary backfill scheduled.'}`,
      });
      if (requestId) {
        setFacebookPolling(true);
      }
    } catch (error) {
      console.error('Failed to schedule Facebook summary backfill', error);
      const payload = error?.response?.data;
      const detail =
        payload?.detail ||
        (typeof payload === 'string' ? payload : '') ||
        Object.values(payload || {}).flat().join(' ') ||
        'Failed to schedule Facebook summary backfill.';
      setFacebookAlert({ severity: 'error', text: detail });
      setFacebookPolling(false);
    } finally {
      setFacebookLoading(false);
    }
  };

  const handleLeadCzvBackfill = async () => {
    if (!hasScope || !hasValidRange || leadCzvLoading) return;
    setLeadCzvLoading(true);
    setLeadCzvAlert(null);
    setLeadCzvStatus(null);
    try {
      const res = await triggerLeadCzvBackfill({
        ...scopePayload,
        start_date: startDate,
        end_date: endDate,
      });
      const data = res?.data || {};
      const requestId = data?.request_id || '';
      setLeadCzvRequestId(requestId);
      setLeadCzvAlert({
        severity: 'success',
        text: `${data?.detail || 'Lead ZIP vertical backfill scheduled.'}`,
      });
      if (requestId) {
        setLeadCzvPolling(true);
      }
    } catch (error) {
      console.error('Failed to schedule lead CZV backfill', error);
      const payload = error?.response?.data;
      const detail =
        payload?.detail ||
        (typeof payload === 'string' ? payload : '') ||
        Object.values(payload || {}).flat().join(' ') ||
        'Failed to schedule lead CZV backfill.';
      setLeadCzvAlert({ severity: 'error', text: detail });
      setLeadCzvPolling(false);
    } finally {
      setLeadCzvLoading(false);
    }
  };

  const handleDeleteDashboardData = async () => {
    if (!hasScope || !hasValidRange || !hasDeleteModels || deleteLoading) return;
    setDeleteLoading(true);
    setDeleteAlert(null);
    setDeleteStatus(null);
    try {
      const res = await triggerDashboardDataDeletion({
        ...scopePayload,
        start_date: startDate,
        end_date: endDate,
        selected_models: deleteSelectedModels,
      });
      const data = res?.data || {};
      const requestId = data?.request_id || '';
      setDeleteRequestId(requestId);
      setDeleteAlert({
        severity: 'success',
        text: `${data?.detail || 'Dashboard data deletion scheduled.'}`,
      });
      if (requestId) {
        setDeletePolling(true);
      }
    } catch (error) {
      console.error('Failed to schedule dashboard data deletion', error);
      const payload = error?.response?.data;
      const detail =
        payload?.detail ||
        (typeof payload === 'string' ? payload : '') ||
        Object.values(payload || {}).flat().join(' ') ||
        'Failed to schedule dashboard data deletion.';
      setDeleteAlert({ severity: 'error', text: detail });
      setDeletePolling(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteBackendData = async () => {
    if (!hasScope || !hasValidRange || backendDeleteLoading) return;
    setBackendDeleteLoading(true);
    setBackendDeleteAlert(null);
    setBackendDeleteStatus(null);
    try {
      const res = await triggerBackendDataDeletion({
        ...scopePayload,
        start_date: startDate,
        end_date: endDate,
      });
      const data = res?.data || {};
      const requestId = data?.request_id || '';
      setBackendDeleteRequestId(requestId);
      setBackendDeleteAlert({
        severity: 'success',
        text: `${data?.detail || 'Backend data deletion scheduled.'}`,
      });
      if (requestId) {
        setBackendDeletePolling(true);
      }
    } catch (error) {
      console.error('Failed to schedule backend data deletion', error);
      const payload = error?.response?.data;
      const detail =
        payload?.detail ||
        (typeof payload === 'string' ? payload : '') ||
        Object.values(payload || {}).flat().join(' ') ||
        'Failed to schedule backend data deletion.';
      setBackendDeleteAlert({ severity: 'error', text: detail });
      setBackendDeletePolling(false);
    } finally {
      setBackendDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ flex: '1 1 auto', p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Data Backfill
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Run Daily Pulse backfills, backend summary recalculations, and deletion jobs by date range for a client or parent company.
          </Typography>
        </Box>

        <Card>
          <CardHeader title="Common Filters" />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              <ClientDropdown
                value={clientId}
                onChange={(value) => {
                  setClientId(value);
                  if (value) {
                    setParentCompanyId('');
                  }
                }}
                label="Client"
              />
              <TextField
                select
                fullWidth
                size="small"
                label="Parent Company"
                value={parentCompanyId}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setParentCompanyId(nextValue);
                  if (nextValue) {
                    setClientId('');
                  }
                }}
                helperText="Optional. Select a parent company to run the page actions across all of its active clients."
              >
                <MenuItem value="">No parent company filter</MenuItem>
                {parentCompanyOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              {parentCompanyId ? (
                <Alert severity="info">
                  Parent company scope is active. Page actions will run across all active child clients unless a card explicitly supports all clients.
                </Alert>
              ) : null}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
              <Typography variant="subtitle2">Daily pulse platforms to import</Typography>
              <FormGroup>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    mb: 0.75,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        checked={platformKeys.every((key) => selectedPlatforms.includes(key))}
                        indeterminate={
                          selectedPlatforms.some((key) => platformKeys.includes(key)) &&
                          !platformKeys.every((key) => selectedPlatforms.includes(key))
                        }
                        onChange={() =>
                          toggleAllModels(setSelectedPlatforms, selectedPlatforms, platformKeys)
                        }
                      />
                    }
                    label={
                      platformKeys.every((key) => selectedPlatforms.includes(key))
                        ? 'Unselect all platforms'
                        : 'Select all platforms'
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {selectedPlatforms.length}/{platformKeys.length} selected
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: 0.25,
                  }}
                >
                  {PLATFORM_OPTIONS.map((platform) => (
                    <FormControlLabel
                      key={platform.key}
                      sx={{ m: 0 }}
                      control={
                        <Checkbox
                          checked={selectedPlatforms.includes(platform.key)}
                          onChange={() =>
                            toggleModel(setSelectedPlatforms, selectedPlatforms, platform.key)
                          }
                        />
                      }
                      label={platform.label}
                    />
                  ))}
                </Box>
              </FormGroup>
              {!hasSelectedPlatforms && !skipPlatformImport ? (
                <Alert severity="warning">
                  Select at least one platform, or enable skip ad-platform import.
                </Alert>
              ) : null}
              {!hasValidRange ? (
                <Alert severity="error">End date must be on or after start date.</Alert>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Daily Pulse Backfill" subheader="Queues collect-report-range with Celery sequential mode." />
          <Divider />
          <CardContent>
            <Stack spacing={1.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipPlatformImport}
                    onChange={(event) => setSkipPlatformImport(event.target.checked)}
                  />
                }
                label="Skip ad-platform import (rebuild from existing DB data only)"
              />
              <Typography variant="subtitle2">Daily Pulse models to rebuild</Typography>
              <FormGroup>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    mb: 0.75,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        checked={dailyModelKeys.every((key) => dailySelectedModels.includes(key))}
                        indeterminate={
                          dailySelectedModels.some((key) => dailyModelKeys.includes(key)) &&
                          !dailyModelKeys.every((key) => dailySelectedModels.includes(key))
                        }
                        onChange={() =>
                          toggleAllModels(setDailySelectedModels, dailySelectedModels, dailyModelKeys)
                        }
                      />
                    }
                    label={
                      dailyModelKeys.every((key) => dailySelectedModels.includes(key))
                        ? 'Unselect all models'
                        : 'Select all models'
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {dailySelectedModels.length}/{dailyModelKeys.length} selected
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 0.25,
                  }}
                >
                  {DAILY_MODELS.map((model) => (
                    <FormControlLabel
                      key={model.key}
                      sx={{ m: 0 }}
                      control={
                        <Checkbox
                          checked={dailySelectedModels.includes(model.key)}
                          onChange={() => toggleModel(setDailySelectedModels, dailySelectedModels, model.key)}
                        />
                      }
                      label={model.label}
                    />
                  ))}
                </Box>
              </FormGroup>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleDailyBackfill}
                  disabled={!hasScope || !hasValidRange || !hasDailyModels || (!skipPlatformImport && !hasSelectedPlatforms) || dailyLoading}
                >
                  {dailyLoading ? 'Scheduling...' : 'Run Daily Pulse Backfill'}
                </Button>
                {dailyPolling ? (
                  <Button variant="outlined" onClick={() => setDailyPolling(false)}>Stop Polling</Button>
                ) : null}
              </Stack>
              {!hasDailyModels ? <Alert severity="warning">Select at least one Daily Pulse model.</Alert> : null}
              {dailyAlert ? <Alert severity={dailyAlert.severity}>{dailyAlert.text}</Alert> : null}
              {dailyStatus ? (
                <>
                  <Alert severity={getStateSeverity(dailyStatus.state)}>
                    State: {dailyStatus.state} | Succeeded: {dailyStatus.succeeded || 0} | Failed: {dailyStatus.failed || 0} | Running: {dailyStatus.running || 0}
                  </Alert>
                  <LinearProgress variant="determinate" value={getDailyProgress(dailyStatus)} />
                  <Typography variant="caption" color="text.secondary">
                    Request ID: {dailyStatus.request_id || dailyRequestId || '—'}
                  </Typography>
                </>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Backend Summary Backfill" subheader="Queues apply_backend_summary_update for each date in the selected range." />
          <Divider />
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Backend Summary models to rebuild</Typography>
              <FormGroup>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    mb: 0.75,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        checked={backendModelKeys.every((key) => backendSelectedModels.includes(key))}
                        indeterminate={
                          backendSelectedModels.some((key) => backendModelKeys.includes(key)) &&
                          !backendModelKeys.every((key) => backendSelectedModels.includes(key))
                        }
                        onChange={() =>
                          toggleAllModels(setBackendSelectedModels, backendSelectedModels, backendModelKeys)
                        }
                      />
                    }
                    label={
                      backendModelKeys.every((key) => backendSelectedModels.includes(key))
                        ? 'Unselect all models'
                        : 'Select all models'
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {backendSelectedModels.length}/{backendModelKeys.length} selected
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 0.25,
                  }}
                >
                  {BACKEND_MODELS.map((model) => (
                    <FormControlLabel
                      key={model.key}
                      sx={{ m: 0 }}
                      control={
                        <Checkbox
                          checked={backendSelectedModels.includes(model.key)}
                          onChange={() => toggleModel(setBackendSelectedModels, backendSelectedModels, model.key)}
                        />
                      }
                      label={model.label}
                    />
                  ))}
                </Box>
              </FormGroup>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleBackendBackfill}
                  disabled={!hasScope || !hasValidRange || !hasBackendModels || backendLoading}
                >
                  {backendLoading ? 'Scheduling...' : 'Run Backend Summary Backfill'}
                </Button>
                {backendPolling ? (
                  <Button variant="outlined" onClick={() => setBackendPolling(false)}>Stop Polling</Button>
                ) : null}
              </Stack>
              {!hasBackendModels ? <Alert severity="warning">Select at least one Backend Summary model.</Alert> : null}
              {backendAlert ? <Alert severity={backendAlert.severity}>{backendAlert.text}</Alert> : null}
              {backendStatus ? (
                <Alert severity={getStateSeverity(backendStatus.state)}>
                  State: {backendStatus.state} | Request ID: {backendStatus.request_id || backendRequestId || '—'}
                </Alert>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Lead ZIP Vertical Backfill"
            subheader="Re-resolves Lead.client_zip_vertical, Lead.client_zip, and territory for all leads in the selected client/date range."
          />
          <Divider />
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Uses the common client and date range filters above. This runs across all leads in the range, not just missing assignments.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleLeadCzvBackfill}
                  disabled={!hasScope || !hasValidRange || leadCzvLoading}
                >
                  {leadCzvLoading ? 'Scheduling...' : 'Run Lead ZIP Vertical Backfill'}
                </Button>
                {leadCzvPolling ? (
                  <Button variant="outlined" onClick={() => setLeadCzvPolling(false)}>
                    Stop Polling
                  </Button>
                ) : null}
              </Stack>
              {leadCzvAlert ? (
                <Alert severity={leadCzvAlert.severity}>{leadCzvAlert.text}</Alert>
              ) : null}
              {leadCzvStatus ? (
                <Alert severity={getStateSeverity(leadCzvStatus.state)}>
                  State: {leadCzvStatus.state} | Request ID: {leadCzvStatus.request_id || leadCzvRequestId || '—'}
                </Alert>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Delete Dashboard Summaries" subheader="Queues delete_client_dashboard_data for the selected range." />
          <Divider />
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Delete only these dashboard models</Typography>
              <FormGroup>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    mb: 0.75,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        checked={deleteModelKeys.every((key) => deleteSelectedModels.includes(key))}
                        indeterminate={
                          deleteSelectedModels.some((key) => deleteModelKeys.includes(key)) &&
                          !deleteModelKeys.every((key) => deleteSelectedModels.includes(key))
                        }
                        onChange={() =>
                          toggleAllModels(setDeleteSelectedModels, deleteSelectedModels, deleteModelKeys)
                        }
                      />
                    }
                    label={
                      deleteModelKeys.every((key) => deleteSelectedModels.includes(key))
                        ? 'Unselect all models'
                        : 'Select all models'
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {deleteSelectedModels.length}/{deleteModelKeys.length} selected
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 0.25,
                  }}
                >
                  {DELETE_MODELS.map((model) => (
                    <FormControlLabel
                      key={model.key}
                      sx={{ m: 0 }}
                      control={
                        <Checkbox
                          checked={deleteSelectedModels.includes(model.key)}
                          onChange={() => toggleModel(setDeleteSelectedModels, deleteSelectedModels, model.key)}
                        />
                      }
                      label={model.label}
                    />
                  ))}
                </Box>
              </FormGroup>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteDashboardData}
                  disabled={!hasScope || !hasValidRange || !hasDeleteModels || deleteLoading}
                >
                  {deleteLoading ? 'Scheduling...' : 'Run Dashboard Data Delete'}
                </Button>
                {deletePolling ? (
                  <Button variant="outlined" onClick={() => setDeletePolling(false)}>Stop Polling</Button>
                ) : null}
              </Stack>
              {!hasDeleteModels ? <Alert severity="warning">Select at least one model to delete.</Alert> : null}
              {deleteAlert ? <Alert severity={deleteAlert.severity}>{deleteAlert.text}</Alert> : null}
              {deleteStatus ? (
                <Alert severity={getStateSeverity(deleteStatus.state)}>
                  State: {deleteStatus.state} | Request ID: {deleteStatus.request_id || deleteRequestId || '—'}
                </Alert>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Delete Backend Data Records"
            subheader="Queues raw BackendData deletion for the selected client or parent-company scope."
          />
          <Divider />
          <CardContent>
            <Stack spacing={1.5}>
              <Alert severity="warning">
                This removes raw backend records for the selected client or parent-company scope, based on `BackendData.created_date`.
              </Alert>
              {parentCompanyId ? (
                <Alert severity="info">
                  Parent company scope is active. The delete will target all child clients in that group.
                </Alert>
              ) : null}
              {!hasScope ? (
                <Alert severity="warning">
                  Select either a client or a parent company in Common Filters.
                </Alert>
              ) : null}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteBackendData}
                  disabled={!hasScope || !hasValidRange || backendDeleteLoading}
                >
                  {backendDeleteLoading ? 'Scheduling...' : 'Run Backend Data Delete'}
                </Button>
                {backendDeletePolling ? (
                  <Button variant="outlined" onClick={() => setBackendDeletePolling(false)}>
                    Stop Polling
                  </Button>
                ) : null}
              </Stack>
              {backendDeleteAlert ? (
                <Alert severity={backendDeleteAlert.severity}>{backendDeleteAlert.text}</Alert>
              ) : null}
              {backendDeleteStatus ? (
                <Alert severity={getStateSeverity(backendDeleteStatus.state)}>
                  State: {backendDeleteStatus.state} | Request ID: {backendDeleteStatus.request_id || backendDeleteRequestId || '—'}
                </Alert>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Facebook Ad Summary Backfill"
            subheader="Queues Facebook ad set/ad insights backfill and optionally refreshes /ads creative metadata into AdSummary."
          />
          <Divider />
          <CardContent>
            <Stack spacing={1.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={facebookAllClients}
                    onChange={(event) => setFacebookAllClients(event.target.checked)}
                  />
                }
                label="Run for all clients"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeFacebookMetadata}
                    onChange={(event) => setIncludeFacebookMetadata(event.target.checked)}
                  />
                }
                label="Include /ads creative metadata refresh"
              />
              {!facebookAllClients && !hasScope ? (
                <Alert severity="warning">
                  Select a client or parent company above, or enable &quot;Run for all clients&quot;.
                </Alert>
              ) : null}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleFacebookBackfill}
                  disabled={!hasValidRange || facebookLoading || (!facebookAllClients && !hasScope)}
                >
                  {facebookLoading ? 'Scheduling...' : 'Run Facebook Summary Backfill'}
                </Button>
                {facebookPolling ? (
                  <Button variant="outlined" onClick={() => setFacebookPolling(false)}>
                    Stop Polling
                  </Button>
                ) : null}
              </Stack>
              {facebookAlert ? (
                <Alert severity={facebookAlert.severity}>{facebookAlert.text}</Alert>
              ) : null}
              {facebookStatus ? (
                <>
                  <Alert severity={getStateSeverity(facebookStatus.state)}>
                    State: {facebookStatus.state} | Succeeded: {facebookStatus.succeeded || 0} |
                    {' '}Failed: {facebookStatus.failed || 0} | Running: {facebookStatus.running || 0}
                  </Alert>
                  <LinearProgress variant="determinate" value={getDailyProgress(facebookStatus)} />
                  <Typography variant="caption" color="text.secondary">
                    Request ID: {facebookStatus.request_id || facebookRequestId || '—'}
                  </Typography>
                </>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
